import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  Plus,
  CalendarRange,
  Search,
  RefreshCw,
  XCircle,
  FileText,
  Settings,
  CalendarDays,
  Clock,
  CheckCircle2,
  Ban,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Card } from '@/shared/components/ui/Card';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useUser } from '@/shared/hooks/useAuth';
import { Link } from 'react-router-dom';
import { isSuperAdminOrOwner } from '@jira-clone/shared-types';
import { canManageHrSettings, isHrDepartmentAdmin } from '../access';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useLeaves, useLeaveMutations, useLeaveBalances, useLeaveTypes } from '../hooks/useHr';
import { SubmitLeaveModal } from '../components/SubmitLeaveModal';
import { LeaveDetailDrawer } from '../components/LeaveDetailDrawer';
import { LeaveCalendar } from '../components/LeaveCalendar';
import { StatTile } from '../components/StatTile';
import {
  DEPT_LABELS,
  LEAVE_STATUS_LABEL,
  LEAVE_STATUS_TONE,
  LEAVE_TYPE_LABELS,
  fmtDate,
} from '../constants';
import type { Leave, LeaveStatus, SubmitLeavePayload } from '../types';

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

const SCOPE_OPTIONS = [
  { value: 'all', label: 'All Requests' },
  { value: 'mine', label: 'My Requests' },
  { value: 'team', label: 'Team Requests' },
];

export function LeavesPage() {
  const user = useUser();
  const roleLower = String(user?.role ?? '').toLowerCase();
  const isHrAdmin = isHrDepartmentAdmin(user);
  const isSA = isSuperAdminOrOwner(user?.role) || roleLower === 'superadmin' || roleLower === 'owner';
  const isApprover = isHrAdmin || isSA;

  const { data: leaves = [], isLoading, isError, isFetching, refetch } = useLeaves();
  const { submitLeave, reviewLeave, cancelLeave } = useLeaveMutations();
  const { data: balanceData } = useLeaveBalances();
  const { data: leaveTypes = [] } = useLeaveTypes();

  const [submitOpen, setSubmitOpen] = useState(false);
  const [selected, setSelected] = useState<Leave | null>(null);
  const [view, setView] = useState<'list' | 'calendar'>('list');

  // Toolbar filter state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeaveStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [scope, setScope] = useState<'all' | 'mine' | 'team'>('all');

  // Build type filter options from dynamic leave types
  const typeFilterOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Types' }];
    if (leaveTypes.length > 0) {
      for (const lt of leaveTypes) {
        options.push({ value: lt.code, label: lt.name });
      }
    } else {
      // Fallback to legacy labels
      options.push(
        { value: 'annual', label: 'Annual' },
        { value: 'sick', label: 'Sick' },
        { value: 'emergency', label: 'Emergency' },
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'other', label: 'Other' },
      );
    }
    return options;
  }, [leaveTypes]);

  // Resolve leave type name from code
  const typeName = (code: string) => {
    const lt = leaveTypes.find((t) => t.code === code);
    return lt?.name ?? LEAVE_TYPE_LABELS[code] ?? code;
  };

  const handleSubmit = (payload: SubmitLeavePayload) => {
    submitLeave.mutate(payload, {
      onSuccess: () => {
        toast.success('Leave request submitted.');
        setSubmitOpen(false);
      },
      onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to submit leave.')),
    });
  };

  const handleReview = (leave: Leave, status: 'approved' | 'rejected') => {
    reviewLeave.mutate(
      { id: leave.id, status },
      {
        onSuccess: () => {
          toast.success(status === 'approved' ? 'Leave approved.' : 'Leave rejected.');
          setSelected(null);
        },
        onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to update leave.')),
      },
    );
  };

  const handleCancel = (leave: Leave) => {
    if (!confirm('Are you sure you want to cancel this leave request?')) return;
    cancelLeave.mutate(
      { id: leave.id },
      {
        onSuccess: () => {
          toast.success('Leave cancelled.');
          setSelected(null);
        },
        onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to cancel leave.')),
      },
    );
  };

  // KPI counts
  const kpis = useMemo(() => {
    let pending = 0;
    let approved = 0;
    let rejected = 0;
    let cancelled = 0;
    for (const l of leaves) {
      if (l.status === 'pending') pending++;
      else if (l.status === 'approved') approved++;
      else if (l.status === 'rejected') rejected++;
      else if (l.status === 'cancelled') cancelled++;
    }
    return { pending, approved, rejected, cancelled };
  }, [leaves]);

  // Client-side filtering
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leaves.filter((l) => {
      if (statusFilter !== 'all' && l.status !== statusFilter) return false;
      if (typeFilter !== 'all' && l.type !== typeFilter) return false;
      if (isApprover && scope === 'mine' && l.user_id !== user?.id) return false;
      if (isApprover && scope === 'team' && l.user_id === user?.id) return false;
      if (q) {
        const hay = [
          l.employee_name,
          l.reason,
          typeName(l.type),
          DEPT_LABELS[l.employee_dept ?? ''] ?? l.employee_dept,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [leaves, search, statusFilter, typeFilter, scope, isApprover, user?.id, leaveTypes]);

  const canApprove = (l: Leave) => isApprover && l.status === 'pending';
  const canCancel = (l: Leave) => {
    if (l.user_id !== user?.id) return false;
    if (l.status === 'pending') return true;
    if (l.status === 'approved') {
      const today = new Date().toISOString().slice(0, 10);
      return l.from_date > today;
    }
    return false;
  };

  const renderActions = (l: Leave) => {
    const actions = [];
    if (canApprove(l)) {
      actions.push(
        <Button key="approve" variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleReview(l, 'approved'); }}>
          Approve
        </Button>,
        <Button key="reject" variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleReview(l, 'rejected'); }}>
          Reject
        </Button>,
      );
    }
    if (canCancel(l)) {
      actions.push(
        <Button key="cancel" variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleCancel(l); }}>
          <XCircle className="h-3.5 w-3.5" /> Cancel
        </Button>,
      );
    }
    if (actions.length === 0) return <span className="text-[11px] text-on-surface-variant/40">—</span>;
    return <div className="flex items-center justify-end gap-1.5">{actions}</div>;
  };

  const columns: Column<Leave>[] = [
    ...(isApprover
      ? [
          {
            key: 'employee',
            header: 'Employee',
            render: (l: Leave) => (
              <div className="min-w-0">
                <div className="font-medium text-on-surface">{l.employee_name ?? '—'}</div>
                <div className="text-[10px] text-on-surface-variant">
                  {DEPT_LABELS[l.employee_dept ?? ''] ?? l.employee_dept}
                </div>
              </div>
            ),
          } as Column<Leave>,
        ]
      : []),
    { key: 'type', header: 'Type', render: (l) => <span>{typeName(l.type)}</span> },
    { key: 'from_date', header: 'From', render: (l) => fmtDate(l.from_date) },
    { key: 'to_date', header: 'To', render: (l) => fmtDate(l.to_date) },
    {
      key: 'days',
      header: 'Days',
      align: 'center',
      render: (l) => (
        <span className="font-semibold">
          {l.days}{l.is_half_day ? ' (½)' : ''}
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (l) => (
        <div className="flex items-center gap-1.5">
          <span className="block max-w-[140px] truncate text-on-surface-variant" title={l.reason || ''}>
            {l.reason || '—'}
          </span>
          {l.document_url && (
            <span title="Document attached">
              <FileText className="h-3.5 w-3.5 text-info" />
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (l) => <Badge tone={LEAVE_STATUS_TONE[l.status] ?? 'neutral'}>{LEAVE_STATUS_LABEL[l.status] ?? l.status}</Badge>,
    },
    { key: 'date', header: 'Submitted', render: (l) => <span className="text-[11px] text-on-surface-variant">{fmtDate(l.created_at)}</span> },
    { key: 'actions', header: 'Actions', align: 'right', render: renderActions },
  ];

  // Balance cards for current user
  const balances = balanceData?.balances ?? [];
  const showBalances = balances.length > 0;

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarRange className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-display-sm font-semibold text-on-surface">Leave Management</h1>
            <p className="text-body-sm text-on-surface-variant">
              {filtered.length} of {leaves.length} request{leaves.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isApprover && (
            <Button
              variant={view === 'calendar' ? 'primary' : 'outline'}
              size="md"
              onClick={() => setView(view === 'calendar' ? 'list' : 'calendar')}
            >
              <CalendarDays className="h-4 w-4" /> {view === 'calendar' ? 'Back to List' : 'Calendar'}
            </Button>
          )}
          {canManageHrSettings(user) && (
            <Link to="/app/settings/hr/leave-types">
              <Button variant="outline" size="md">
                <Settings className="h-4 w-4" /> Leave Policy
              </Button>
            </Link>
          )}
          <Button onClick={() => setSubmitOpen(true)} size="md">
            <Plus className="h-4 w-4" /> Submit Leave
          </Button>
        </div>
      </div>

      {view === 'calendar' && isApprover ? (
        <LeaveCalendar />
      ) : (
      <>
      {/* Balance cards — show own balance to all users */}
      {showBalances && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {balances.map((b) => {
            const lt = leaveTypes.find((t) => t.code === b.leave_type_code);
            const label = lt?.name ?? LEAVE_TYPE_LABELS[b.leave_type_code] ?? b.leave_type_code;
            const color = lt?.color ?? '#3b82f6';
            return (
              <Card key={b.id} className="relative overflow-hidden px-3 py-2">
                <div className="absolute left-0 top-0 h-full w-1" style={{ backgroundColor: color }} />
                <p className="text-[10px] font-medium uppercase text-on-surface-variant">{label}</p>
                <p className="text-lg font-bold text-on-surface">{b.available}</p>
                <p className="text-[10px] text-on-surface-variant">
                  of {b.allocated + b.carried_forward} · {b.used} used · {b.pending} pending
                </p>
              </Card>
            );
          })}
        </div>
      )}

      {/* KPI dashboard */}
      {leaves.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatTile icon={Clock} label="Pending" value={kpis.pending} tone="warning" index={0} />
          <StatTile icon={CheckCircle2} label="Approved" value={kpis.approved} tone="success" index={1} />
          <StatTile icon={XCircle} label="Rejected" value={kpis.rejected} tone="danger" index={2} />
          <StatTile icon={Ban} label="Cancelled" value={kpis.cancelled} tone="info" index={3} />
        </div>
      )}

      {/* Enterprise toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[200px] flex-1">
          <Input
            name="leave-search"
            placeholder={isApprover ? 'Search employee, reason, type…' : 'Search reason, type…'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
        <div className="w-[160px]">
          <Select
            name="leave-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as LeaveStatus | 'all')}
            options={STATUS_FILTER_OPTIONS}
          />
        </div>
        <div className="w-[150px]">
          <Select
            name="leave-type-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            options={typeFilterOptions}
          />
        </div>
        {isApprover && (
          <div className="w-[160px]">
            <Select
              name="leave-scope-filter"
              value={scope}
              onChange={(e) => setScope(e.target.value as 'all' | 'mine' | 'team')}
              options={SCOPE_OPTIONS}
            />
          </div>
        )}
        <Button variant="outline" size="md" onClick={() => refetch()} loading={isFetching} aria-label="Refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : isError ? (
        <EmptyState title="Failed to load leaves" description="Check your connection or try refreshing." />
      ) : (
        <Table
          columns={columns}
          data={filtered}
          rowKey={(l) => l.id}
          onRowClick={(l) => setSelected(l)}
          empty={leaves.length === 0 ? 'No leave requests found.' : 'No requests match your filters.'}
        />
      )}

      <SubmitLeaveModal
        open={submitOpen}
        loading={submitLeave.isPending}
        onClose={() => setSubmitOpen(false)}
        onSubmit={handleSubmit}
      />
      </>
      )}

      <LeaveDetailDrawer
        leave={selected}
        open={!!selected}
        isPriv={isApprover}
        onClose={() => setSelected(null)}
        actions={
          selected ? (
            <>
              {canApprove(selected) && (
                <>
                  <Button
                    variant="primary"
                    size="sm"
                    loading={reviewLeave.isPending}
                    onClick={() => handleReview(selected, 'approved')}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    loading={reviewLeave.isPending}
                    onClick={() => handleReview(selected, 'rejected')}
                  >
                    Reject
                  </Button>
                </>
              )}
              {canCancel(selected) && (
                <Button
                  variant="ghost"
                  size="sm"
                  loading={cancelLeave.isPending}
                  onClick={() => handleCancel(selected)}
                >
                  <XCircle className="h-3.5 w-3.5" /> Cancel Leave
                </Button>
              )}
            </>
          ) : undefined
        }
      />
    </div>
  );
}

